import getEnvironmentSettings from '../configs/env-conf-loader';

import { ApolloClient, InMemoryCache, ApolloLink, MutationOptions, OperationVariables, GraphQLRequest, QueryOptions, ApolloQueryResult, MaybeMasked, FetchResult } from '@apollo/client/core';
import { ErrorLink, ErrorResponse } from '@apollo/client/link/error';
import { createHttpLink } from '@apollo/client/link/http';
import { setContext } from '@apollo/client/link/context';
import { AuthService } from "./../services/auth.service";
import { StorageService } from './storageService.service';
import { StateService, UIRouterGlobals, UrlService } from '@uirouter/angular';
import { inject, Injectable, Injector } from '@angular/core';
import { NetworkError } from '@apollo/client/errors';
import { TrackingService } from './tracking.service';
import { isDocumentNode } from '@apollo/client/utilities';

@Injectable({ providedIn: 'root' })
export class graphqlClientService {

  private readonly GRAPHQL_SERVER_URL: string = getEnvironmentSettings().graphQlMiddleware;
  private readonly $url = inject(UrlService);
  private readonly $routerGlobals = inject(UIRouterGlobals);
  private readonly $state = inject(StateService);
  private readonly _auth = inject(AuthService);
  private readonly _storage = inject(StorageService);
  private readonly _injector = inject(Injector);

  /**
   * creates a link and executes query to a GraphQL server.
   */
  public query<T, V = OperationVariables>(q: QueryOptions<V>["query"], url?: string): Promise<ApolloQueryResult<MaybeMasked<{ [key: string]: T }>>>
  public query<T, V = OperationVariables>(q: QueryOptions<V>["query"], v: QueryOptions<V>["variables"], url?: string): Promise<ApolloQueryResult<MaybeMasked<{ [key: string]: T }>>>
  public query<T, V = OperationVariables>(q: QueryOptions<V>, url?: string): Promise<ApolloQueryResult<MaybeMasked<{ [key: string]: T }>>>
  public query<T, V = OperationVariables>(
    q: QueryOptions<V> | QueryOptions<V>["query"],
    v: QueryOptions<V>["variables"] | string = this.GRAPHQL_SERVER_URL,
    url: string = this.GRAPHQL_SERVER_URL
  ): Promise<ApolloQueryResult<MaybeMasked<{ [key: string]: T }>>> {
    if (isDocumentNode(q) && typeof v == "string")
        return this._query({ query: q }, v);
    else if (isDocumentNode(q) && typeof v != "string")
        return this._query({ query: q, variables: v }, url);
    else if (!isDocumentNode(q) && typeof v == "string")
        return this._query(q, v);
  }

  private _query<T, V = OperationVariables>(q: QueryOptions<V>, url: string): Promise<ApolloQueryResult<MaybeMasked<{ [key: string]: T }>>> {
    const client = this._createClient(url);
    return client.query<{ [key: string]: T }, V>(q);
  }

  /**
   * Creates a link chain and executes a mutation to a GraphQL server.
   */
  public mutate<T, V = OperationVariables>(m: MutationOptions<V>["mutation"], url?: string): Promise<ApolloQueryResult<MaybeMasked<{ [key: string]: T }>>>
  public mutate<T, V = OperationVariables>(m: MutationOptions<V>["mutation"], v: MutationOptions<V>["variables"], url?: string): Promise<ApolloQueryResult<MaybeMasked<{ [key: string]: T }>>>
  public mutate<T, V = OperationVariables>(m: MutationOptions<V>, url?: string): Promise<ApolloQueryResult<MaybeMasked<{ [key: string]: T }>>>
  public mutate<T, V = OperationVariables>(
    m: MutationOptions<V> | MutationOptions<V>["mutation"],
    v: MutationOptions<V>["variables"] | string = this.GRAPHQL_SERVER_URL,
    url: string = this.GRAPHQL_SERVER_URL
  ): Promise<FetchResult<T>> {
    switch (true) {
      case isDocumentNode(m) && typeof v == "string": return this._mutate({ mutation: m }, v);
      case isDocumentNode(m) && typeof v != "string": return this._mutate({ mutation: m, variables: v }, url);
      case !isDocumentNode(m) && typeof v == "string": return this._mutate(m, v);
    }
  }

  private _mutate(options: MutationOptions<any, OperationVariables>, url: string) {
    const client = this._createClient(url);
    return client.mutate(options);
  }

  private _setContextHandler(_: GraphQLRequest, context) {
    // get the authentication token from local storage if it exists
    const token = this._storage.get.token();
    // return the headers to the context so httpLink can read them
    const headers = context.headers || {};
    return {
      headers: {
        ...headers,
        authorization: token ? token.replace(/"/g, '') : ""
      }
    };
  }

  private _createClient(url: string) {
    const httpLink = createHttpLink({ uri: url });
    const authLink = setContext(this._setContextHandler.bind(this));
    const errorLink = new ErrorLink((res) => this._onErrorHandler(res, url));
    return new ApolloClient({
      link: ApolloLink.from([authLink, errorLink, httpLink]),
      cache: new InMemoryCache()
    });
  }

  private _onErrorHandler(res: ErrorResponse, url: string) {
    // @ts-ignore
    if (res.networkError?.statusCode == 400) return this._track(res.networkError, url);
    // @ts-ignore
    else if (res.networkError?.statusCode == 401) return this._logout();
    //@ts-ignore
    else if (res.networkError?.statusCode == 403) return this._unauthorized(res.networkError);

    else if (
      //@ts-ignore
      res.networkError?.statusCode == 500 ||
      //@ts-ignore
      res.networkError?.statusCode == 502 ||
      //@ts-ignore
      res.networkError?.statusCode == 503
    ) {
      this._track(res.networkError, url);
      //@ts-ignore
      this._serverError(res.networkError.statusCode, res.networkError);
    }

    if (res.graphQLErrors) {
      res.graphQLErrors.map(error => {
        switch (error.extensions.code) {
          case 'UNAUTHENTICATED':
            return this._logout();
          case 'UNAUTHORIZED':
            return this._unauthorized(res.graphQLErrors);
        }
      });
    }
  }

  private _track(error: NetworkError, url: string) {
    const tracking = this._injector.get(TrackingService);
    tracking.trackError((error as any)?.statusCode, url);
  }

  private _logout() {
    this._storage.session.set.lastUrl(this.$url.url());
    this._storage.session.set.lastUrlParts(this.$url.parts());
    this._auth.logout();
  }

  private _unauthorized(error: NetworkError | ErrorResponse["graphQLErrors"]) {
    if (this.$routerGlobals.transition) this.$routerGlobals.transition.abort();
    this.$state.go('error.403', { ...this.$routerGlobals.params, error: error });
  }

  private _serverError(status: 500 | 502 | 503, error: NetworkError) {
    if (this.$routerGlobals.transition) this.$routerGlobals.transition.abort();
    this.$state.go(`error.${status}`, { ...this.$routerGlobals.params, error: error })
  }
}
